import { Avatar } from '@mui/joy';
import { User } from '@phosphor-icons/react';
import { t } from 'i18next';

import { AutocompleteFilter } from 'core/components/AgGridTable';
import { EMPTY, NON_EMPTY } from 'core/constants';
import { AvatarNameCell, ChipCell, FlexBox, LinkCell, StatusCell } from 'core/elements';
import { formatDate } from 'core/utils';
import { CorporateUserStatus } from 'corporate/enums';
import { corporateUserService } from 'corporate/services';
import { IDiscipline } from 'corporate/types';
import { oldBomPartListPath } from 'jobs/routes/routePaths';
import { IJob, IJobStatus } from 'jobs/types';
import { getStatusColorMap } from 'jobs/utils';
import { supportRequestJobListPath } from 'support/routes/routePaths';
import { Color, Size, Variant } from 'theme/enums';

import { RequestedByCell } from './cells';

export const columnDef = (jobStatuses?: IJobStatus[], disciplines?: IDiscipline[]) => [
  {
    colId: 'status',
    headerName: t('headerStatus'),
    field: 'status',
    filter: 'agSetColumnFilter',
    filterParams: {
      values: jobStatuses || [],
      valueFormatter: ({ value }) => value?.name,
      keyCreator: ({ value }) => value?.id,
    },
    cellRenderer: StatusCell,
    cellRendererParams: {
      colorMap: getStatusColorMap(jobStatuses?.map((status) => status.name) || []),
    },
  },
  {
    colId: 'id',
    headerName: t('headerJobId'),
    field: 'id',
    filter: 'agNumberColumnFilter',
    cellClass: 'paddingSmall',
    minWidth: 80,
    width: 100,
    cellRenderer: LinkCell,
    cellRendererParams: {
      getPath: (data: IJob) => oldBomPartListPath(data.projectId, data.id),
    },
  },
  {
    colId: 'name',
    headerName: t('headerJobName'),
    field: 'name',
    filter: 'agTextColumnFilter',
  },
  {
    colId: 'projectName',
    headerName: t('headerProject'),
    field: 'projectName',
    filter: 'agTextColumnFilter',
  },
  {
    colId: 'supportUserName',
    headerName: t('headerAssignedTo'),
    field: 'supportUserName',
    filter: AutocompleteFilter,
    filterParams: {
      autocompleteProps: {
        getOptions: corporateUserService.getCorporateUsersAutocomplete,
        placeholder: t('placeholderSelectUser'),
        requestPayload: {
          filters: {
            status: [CorporateUserStatus.ACTIVE],
          },
        },
      },
      labelEmpty: t('labelNotAssigned'),
      labelNonEmpty: t('labelAssigned'),
    },
    cellRenderer: AvatarNameCell,
    cellRendererParams: {
      colId: 'supportUserName',
      displayEmpty: () => (
        <FlexBox sx={{ gap: 1 }}>
          <Avatar color={Color.NEUTRAL} size={Size.SM} variant={Variant.SOLID}>
            <User weight="fill" />
          </Avatar>
          {t('labelNotAssigned')}
        </FlexBox>
      ),
    },
  },
  {
    colId: 'supportRequestId',
    headerName: t('headerSupportRequestId'),
    field: 'supportRequestId',
    filter: 'agTextColumnFilter',
    cellRenderer: LinkCell,
    cellRendererParams: {
      getPath: (data: IJob) => supportRequestJobListPath(data.supportRequestId),
    },
  },
  {
    colId: 'disciplineName',
    headerName: t('headerDiscipline'),
    field: 'disciplineName',
    filter: 'agSetColumnFilter',
    filterParams: {
      values: [
        { id: EMPTY, name: t('labelEmptyDiscipline') },
        { id: NON_EMPTY, name: t('labelNonEmptyDiscipline') },
        ...(disciplines || []),
      ],
      valueFormatter: ({ value }) => value?.name,
      keyCreator: ({ value }) => value?.id,
    },
    cellRenderer: ChipCell,
  },
  {
    colId: 'requestedDate',
    headerName: t('headerRequestedDate'),
    field: 'requestedDate',
    filter: 'agDateColumnFilter',
    valueFormatter: ({ value }) => formatDate(value),
  },
  {
    colId: 'createdAt',
    headerName: t('headerCreated'),
    field: 'createdAt',
    filter: 'agDateColumnFilter',
    valueFormatter: ({ value }) => formatDate(value),
  },
  {
    colId: 'ownerAddressName',
    headerName: t('headerLocation'),
    field: 'ownerAddressName',
    filter: 'agTextColumnFilter',
  },
  {
    colId: 'ownerRegionName',
    headerName: t('headerRegion'),
    field: 'ownerRegionName',
    filter: 'agTextColumnFilter',
  },
  {
    colId: 'externalLocationRegion',
    headerName: t('headerTheatre'),
    field: 'externalLocationRegion',
    filter: 'agTextColumnFilter',
  },
  {
    colId: 'externalLocationCountry',
    headerName: t('headerCountry'),
    field: 'externalLocationCountry',
    filter: 'agTextColumnFilter',
  },
  {
    colId: 'ownerUserName',
    headerName: t('headerOwner'),
    field: 'ownerUserName',
    filter: AutocompleteFilter,
    filterParams: {
      autocompleteProps: {
        getOptions: corporateUserService.getCorporateUsersAutocomplete,
        placeholder: t('placeholderSelectUser'),
      },
    },
    cellRendererParams: {
      maxRows: 2,
    },
  },
  {
    colId: 'requesterUserName',
    headerName: t('headerRequestedBy'),
    field: 'requesterUserName',
    filter: AutocompleteFilter,
    filterParams: {
      autocompleteProps: {
        getOptions: corporateUserService.getCorporateUsersAutocomplete,
        placeholder: t('placeholderSelectUser'),
      },
    },
    cellRenderer: RequestedByCell,
  },

  {
    colId: 'completedDate',
    headerName: t('headerCompletedDate'),
    field: 'completedDate',
    filter: 'agDateColumnFilter',
    valueFormatter: ({ value }) => formatDate(value),
  },
];
